import { useQuery } from "@apollo/client";
import {
  GET_TICKET_PROVIDER_DEFINITIONS,
  GET_TICKET_PROVIDERS,
} from "./TicketingSystemsApi";
import React, { useContext, useEffect, useState } from "react";
import {
  useIntegrationManagement,
  Breadcrumbs,
} from "@SeemplicityDev/shared/integrations";
import {
  AutomationSystemsContext,
  AutomationSystemsContextType,
} from "./AutomationSystemsWrapper/AutomationSystemsProvider";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSystemDefaults } from "./useSystemDefaults";
import TicketingSystemInstanceDetails from "./TicketingSystemInstanceDetails/TicketingSystemInstanceDetails";
import TicketingSystemIntegrationDetails from "./TicketingSystemIntegrationDetails";
import TicketingSystemsIntegrations from "./TicketingSystemIntegrations";
import {
  useDocumentTitle,
  SidebarDisplayAreaContent,
} from "@SeemplicityDev/shared";

export const SEEMPLICITY_PROVIDER_TYPE = "seemplicity";

export type AutomationSystemType = "ticketing" | "notifications";

export interface AutomationSystemsProps {
  systemType: AutomationSystemType;
  title: string;
  className?: string;
  supportedProvidersTypes?: string[];
  children?: React.ReactNode;
  integrationDetailsAlertText?: string | React.ReactNode;
}

// GraphQL: GET_TICKET_PROVIDER_DEFINITIONS
interface TicketProviderDefinitionNode {
  id?: string;
  type?: string;
  status?: string;
  test_connection_supported?: boolean;
  instructions?: unknown;
  sections?: unknown;
}

interface TicketProviderDefinitionsResponse {
  ticket_provider_definitions?: {
    edges?: Array<{ node?: TicketProviderDefinitionNode }>;
  };
}

// GraphQL: GET_TICKET_PROVIDERS
interface TicketProviderNode {
  id?: string;
  type?: string;
  known_identifier?: string;
  display?: string;
  is_installed?: boolean;
  supports_editing?: boolean;
  instructions?: unknown;
  configuration?: unknown;
  sections?: unknown;
}

interface TicketProvidersResponse {
  ticket_providers?: {
    edges?: Array<{ node?: TicketProviderNode }>;
  };
}

// Definition data keyed by provider type, merged into the integrations list.
interface ProviderDefinitionEntry {
  unavailable: boolean;
  id?: string;
  sections?: unknown;
  test_connection_supported?: boolean;
  instructions?: unknown;
}

type DefinitionsMap = Record<string, ProviderDefinitionEntry>;

// Instance data keyed by provider type, merged into the integrations list.
interface ProviderInstancesEntry {
  hasAtLeastOneInstance: boolean;
  isEnabled: boolean;
  instances: TicketProviderNode[];
}

type InstancesMap = Record<string, ProviderInstancesEntry>;

// systemDefaults entries are loosely shaped; only `type` is read here, the rest
// is spread through to the merged integration object consumed by .js children.
type SystemDefaultItem = { type: string } & Record<string, unknown>;

type IntegrationToShow = Record<string, unknown>;

// Minimal shape required by the shared `sortIntegrations` generic constraint
// (SortableIntegration). The index signature keeps it assignable to the loose
// IntegrationToShow rows stored in state.
type SortableIntegrationItem = {
  name: string;
  hasAtLeastOneInstance?: boolean;
  unavailable?: boolean;
  [key: string]: unknown;
};

export const AutomationSystems: React.FC<AutomationSystemsProps> = ({
  systemType,
  title,
  className = "sidebar-display-area-wrapper",
  supportedProvidersTypes,
  children,
  integrationDetailsAlertText,
}) => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { editTicketingSystemAllowed } = useContext(
    AutomationSystemsContext
  ) as AutomationSystemsContextType;
  const { loading: loadingDefinitions, data: ticketingSystemsDefinitionsData } =
    useQuery<TicketProviderDefinitionsResponse>(
      GET_TICKET_PROVIDER_DEFINITIONS
    );
  const {
    loading: loadingProviders,
    data: ticketingSystemsData,
    error: providersError,
  } = useQuery<TicketProvidersResponse>(GET_TICKET_PROVIDERS, {
    variables: {
      filters: {
        not: { known_identifier: SEEMPLICITY_PROVIDER_TYPE },
        ...(Array.isArray(supportedProvidersTypes) &&
        supportedProvidersTypes.length > 0
          ? { type_in: supportedProvidersTypes }
          : {}),
      },
    },
  });

  const { systemDefaults } = useSystemDefaults();
  const { sortIntegrations } = useIntegrationManagement();

  const [systemsToShow, setSystemsToShow] = useState<IntegrationToShow[]>();
  const [definitionsData, setDefinitionsData] = useState<
    DefinitionsMap | undefined
  >(undefined);

  useEffect(() => {
    if (!ticketingSystemsDefinitionsData) {
      return;
    }
    let defs: DefinitionsMap = {};
    ticketingSystemsDefinitionsData.ticket_provider_definitions?.edges?.forEach(
      (def) => {
        defs[def.node?.type as string] = {
          // Mirrors DataSourcesUtils ACTIVE_CS pattern: ACTIVE_CS = visible but user cannot integrate independently.
          unavailable: def.node?.status === "ACTIVE_CS",
          id: def?.node?.id,
          sections: def?.node?.sections,
          test_connection_supported: def?.node?.test_connection_supported,
          instructions: def?.node?.instructions,
        };
      }
    );

    setDefinitionsData(defs);
  }, [ticketingSystemsDefinitionsData]);

  const [instancesData, setInstancesData] = useState<InstancesMap | undefined>(
    undefined
  );
  useEffect(() => {
    if (!ticketingSystemsData && !providersError) {
      return;
    }
    let instances: InstancesMap = {};
    ticketingSystemsData?.ticket_providers?.edges?.forEach(
      (providerInstance) => {
        const providerType = providerInstance.node?.type as string;
        if (!instances[providerType]) {
          instances[providerType] = {
            hasAtLeastOneInstance: true,
            isEnabled: true,
            instances: [],
          };
        }
        if (providerInstance.node) {
          instances[providerType].instances.push(providerInstance.node);
        }
      }
    );

    setInstancesData(instances);
  }, [ticketingSystemsData, providersError]);

  useEffect(() => {
    if (
      (!ticketingSystemsData && !providersError) ||
      !definitionsData ||
      !instancesData ||
      !systemType
    ) {
      return;
    }
    // `sortIntegrations` requires its generic to extend SortableIntegration
    // (`name: string`, optional `hasAtLeastOneInstance`/`unavailable`). The
    // merged provider objects carry these at runtime; cast the array (via
    // `unknown`, since the loose Record index type isn't structurally
    // assignable) so the generic infers a compatible item type.
    let automationProviders = sortIntegrations(
      ((systemDefaults[systemType] as SystemDefaultItem[]) || [])
        .filter((item) => {
          if (
            !Array.isArray(supportedProvidersTypes) ||
            supportedProvidersTypes.length === 0
          ) {
            return true;
          }
          return supportedProvidersTypes.includes(item.type);
        })
        .map((item) => {
          return {
            ...item,
            ...definitionsData[item.type],
            ...instancesData[item.type],
          };
        }) as unknown as SortableIntegrationItem[]
    );
    setSystemsToShow(automationProviders);
  }, [
    definitionsData,
    instancesData,
    ticketingSystemsData,
    providersError,
    systemType,
  ]);

  useDocumentTitle({ title: t(title) });

  return (
    <div className={className}>
      {children || (
        <div className={"sidebar-display-area-title-wrap"}>
          <div className={"sidebar-display-area-title"}>
            <Breadcrumbs
              integrations={systemsToShow}
              basePath={path}
              baseText={t(title)}
              instanceDisplayProperty={"display"}
            />
          </div>
        </div>
      )}

      <SidebarDisplayAreaContent>
        <Switch>
          <Route exact path={`${path}`}>
            <TicketingSystemsIntegrations
              systemsToShow={systemsToShow || []}
              mutateAllowed={editTicketingSystemAllowed}
              loadingDefinitions={loadingDefinitions}
              loadingProviders={loadingProviders}
            />
          </Route>
          <Route exact path={`${path}/:integrationId`}>
            <TicketingSystemIntegrationDetails
              integrationDetailsAlertText={integrationDetailsAlertText}
              integrations={systemsToShow || []}
              loading={loadingDefinitions || loadingProviders}
              mutateAllowed={editTicketingSystemAllowed}
            />
          </Route>
          <Route exact path={`${path}/:integrationId/:instanceId`}>
            <TicketingSystemInstanceDetails
              integrations={systemsToShow || []}
              loadingTSInstances={loadingProviders}
            />
          </Route>
        </Switch>
      </SidebarDisplayAreaContent>
    </div>
  );
};
